import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fieldSettingAPI } from '../services/api';
import { useToast } from '../contexts/ToastContext';
import {
  Edit2, Eye, EyeOff, Save, RotateCcw, ChevronDown, ChevronUp,
  Info, Check, X, Settings
} from 'lucide-react';
import Button from './Button';

const FieldCustomization = ({ cemeteryId, cemeteryName }) => {
  const queryClient = useQueryClient();
  const { success, error: showError } = useToast();
  
  const [editingField, setEditingField] = useState(null);
  const [customLabelInput, setCustomLabelInput] = useState('');
  const [expandedCategories, setExpandedCategories] = useState({
    basic: true,
    mausoleum: true,
    dead: false,
    acquaintance: false,
    cost: false,
    document: false,
    stone: false,
    engraving: false,
    custom: false
  });
  
  // 필드 설정 조회
  const { data: fieldSettings, isLoading } = useQuery({
    queryKey: ['field-settings', cemeteryId],
    queryFn: () => fieldSettingAPI.getFieldSettingsByCemetery(cemeteryId),
    enabled: !!cemeteryId,
    select: (response) => {
      console.log('필드 설정 API 응답:', response.data);
      return response.data.data; // { success: true, data: {...} }에서 실제 data 추출
    }
  });

  // 필드 설정 일괄 업데이트 뮤테이션
  const updateSettingsMutation = useMutation({
    mutationFn: ({ settings }) => 
      fieldSettingAPI.updateFieldSettings(cemeteryId, settings),
    onSuccess: () => {
      success('필드 설정이 저장되었습니다.');
      queryClient.invalidateQueries(['field-settings', cemeteryId]);
    },
    onError: (error) => {
      showError('필드 설정 저장에 실패했습니다.');
      console.error('필드 설정 저장 실패:', error);
    }
  });

  // 필드 명칭 업데이트 뮤테이션
  const updateLabelMutation = useMutation({
    mutationFn: ({ fieldCode, customLabel }) => 
      fieldSettingAPI.updateFieldLabel(cemeteryId, fieldCode, customLabel),
    onSuccess: () => {
      success('필드 명칭이 업데이트되었습니다.');
      queryClient.invalidateQueries(['field-settings', cemeteryId]);
      setEditingField(null);
      setCustomLabelInput('');
    },
    onError: (error) => {
      showError('필드 명칭 업데이트에 실패했습니다.');
      console.error('필드 명칭 업데이트 실패:', error);
    }
  });

  // 필드 사용 여부 토글 뮤테이션
  const toggleEnabledMutation = useMutation({
    mutationFn: ({ fieldCode, isEnabled }) => 
      fieldSettingAPI.toggleFieldEnabled(cemeteryId, fieldCode, isEnabled),
    onSuccess: () => {
      queryClient.invalidateQueries(['field-settings', cemeteryId]);
    },
    onError: (error) => {
      showError('필드 사용 여부 변경에 실패했습니다.');
      console.error('필드 사용 여부 변경 실패:', error);
    }
  });

  // 필드 설정 삭제 (기본값으로 되돌리기) 뮤테이션
  const deleteSettingMutation = useMutation({
    mutationFn: (fieldCode) => 
      fieldSettingAPI.deleteFieldSetting(cemeteryId, fieldCode),
    onSuccess: () => {
      success('필드가 기본값으로 초기화되었습니다.');
      queryClient.invalidateQueries(['field-settings', cemeteryId]);
    },
    onError: (error) => {
      showError('필드 초기화에 실패했습니다.');
      console.error('필드 초기화 실패:', error);
    }
  });

  // 카테고리 한글 명칭
  const categoryNames = {
    basic: '기본 정보',
    mausoleum: '묘소 정보',
    dead: '사망자 정보',
    acquaintance: '연고자 정보',
    cost: '비용 정보',
    document: '서류 정보',
    stone: '석물 정보',
    engraving: '각자 정보',
    custom: '커스텀 필드'
  };

  // 카테고리 토글
  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  // 필드 명칭 수정 시작
  const startEditingLabel = (field) => {
    setEditingField(field.field_code);
    setCustomLabelInput(field.custom_label || field.default_label);
  };

  // 필드 명칭 저장
  const saveLabel = (fieldCode) => {
    if (!customLabelInput.trim()) {
      showError('필드 명칭을 입력해주세요.');
      return;
    }
    updateLabelMutation.mutate({ fieldCode, customLabel: customLabelInput });
  };

  // 필드 명칭 수정 취소
  const cancelEditing = () => {
    setEditingField(null);
    setCustomLabelInput('');
  };

  // 필드 사용 여부 토글
  const toggleFieldEnabled = (fieldCode, currentStatus) => {
    toggleEnabledMutation.mutate({ 
      fieldCode, 
      isEnabled: !currentStatus 
    });
  };

  // 필드를 기본값으로 되돌리기
  const resetFieldToDefault = (fieldCode) => {
    if (window.confirm('이 필드를 기본 설정으로 되돌리시겠습니까?')) {
      deleteSettingMutation.mutate(fieldCode);
    }
  };

  if (isLoading) {
    return (
      <div className="card">
        <div className="card-body">
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
            <p className="mt-4 text-gray-600">필드 설정을 불러오는 중...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!fieldSettings || !fieldSettings.grouped_fields) {
    return (
      <div className="card">
        <div className="card-body">
          <div className="text-center py-8 text-gray-500">
            <Info size={48} className="mx-auto mb-4 text-gray-300" />
            <p>필드 설정을 불러올 수 없습니다.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="card-header">
        <div className="flex-between">
          <div>
            <h3 className="text-lg font-semibold flex items-center space-x-2">
              <Settings size={20} />
              <span>{cemeteryName} - 필드 커스터마이징</span>
            </h3>
            <p className="text-sm text-gray-600 mt-1">
              계약 등록 폼의 필드 명칭과 사용 여부를 설정할 수 있습니다.
            </p>
          </div>
        </div>
      </div>

      <div className="card-body">
        <div className="space-y-4">
          {Object.entries(fieldSettings.grouped_fields).map(([category, fields]) => (
            <div key={category} className="border border-gray-200 rounded-lg overflow-hidden">
              {/* 카테고리 헤더 */}
              <button
                onClick={() => toggleCategory(category)}
                className="w-full bg-gray-50 hover:bg-gray-100 px-4 py-3 flex items-center justify-between transition-colors"
              >
                <div className="flex items-center space-x-2">
                  {expandedCategories[category] ? (
                    <ChevronDown size={18} className="text-gray-600" />
                  ) : (
                    <ChevronUp size={18} className="text-gray-600" />
                  )}
                  <span className="font-semibold text-gray-900">
                    {categoryNames[category] || category}
                  </span>
                  <span className="text-sm text-gray-500">
                    ({fields.filter(f => f.is_enabled).length}/{fields.length})
                  </span>
                </div>
              </button>

              {/* 카테고리 필드 목록 */}
              {expandedCategories[category] && (
                <div className="divide-y divide-gray-200">
                  {fields.map((field) => (
                    <div
                      key={field.field_code}
                      className="px-4 py-3 hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center justify-between">
                        {/* 왼쪽: 필드 정보 */}
                        <div className="flex-1 mr-4">
                          <div className="flex items-center space-x-3">
                            {/* 사용 여부 토글 */}
                            <button
                              onClick={() => toggleFieldEnabled(field.field_code, field.is_enabled)}
                              className={`flex items-center justify-center w-10 h-10 rounded transition-colors ${
                                field.is_enabled
                                  ? 'bg-green-100 hover:bg-green-200 text-green-600'
                                  : 'bg-gray-100 hover:bg-gray-200 text-gray-400'
                              }`}
                              title={field.is_enabled ? '사용 중' : '사용 안 함'}
                            >
                              {field.is_enabled ? <Eye size={18} /> : <EyeOff size={18} />}
                            </button>

                            {/* 필드 명칭 편집 */}
                            <div className="flex-1">
                              {editingField === field.field_code ? (
                                <div className="flex items-center space-x-2">
                                  <input
                                    type="text"
                                    value={customLabelInput}
                                    onChange={(e) => setCustomLabelInput(e.target.value)}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') saveLabel(field.field_code);
                                      if (e.key === 'Escape') cancelEditing();
                                    }}
                                    className="input flex-1"
                                    placeholder="필드 명칭 입력"
                                    autoFocus
                                  />
                                  <Button
                                    variant="success"
                                    size="sm"
                                    onClick={() => saveLabel(field.field_code)}
                                    icon={Check}
                                  />
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={cancelEditing}
                                    icon={X}
                                  />
                                </div>
                              ) : (
                                <div>
                                  <div className="flex items-center space-x-2">
                                    <span className="font-medium text-gray-900">
                                      {field.label}
                                    </span>
                                    {field.has_custom_setting && (
                                      <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded">
                                        커스텀
                                      </span>
                                    )}
                                  </div>
                                  <div className="text-xs text-gray-500 mt-1">
                                    코드: {field.field_code} | 기본명: {field.default_label}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* 오른쪽: 액션 버튼 */}
                        <div className="flex items-center space-x-2">
                          {editingField !== field.field_code && (
                            <>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => startEditingLabel(field)}
                                icon={Edit2}
                              >
                                명칭 수정
                              </Button>
                              {field.has_custom_setting && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => resetFieldToDefault(field.field_code)}
                                  icon={RotateCcw}
                                >
                                  초기화
                                </Button>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default FieldCustomization;

